/**
 * useHydra — React hook that ATTACHES to the HydraClient fleet (it no longer
 * owns its lifecycle). The fleet is a module-level singleton; the hook
 * ref-counts its mounts and the sockets are torn down only after the LAST
 * unmount AND a grace window with no remount (see below). Feeds data into the
 * Zustand marketStore.
 *
 * Why a singleton: the connection used to be created on mount and stopped on
 * unmount inside this hook's effect. But this hook lives in `AuthedShell`, which
 * `RequireAuth` drops on any momentarily-null `user` (in-flight reauth), and
 * React's StrictMode double-mounts in dev — so every such blip tore the WS down
 * and reopened it. Combined with a `type:'module'` SharedWorker that falls back
 * to a direct socket PER WINDOW where unsupported, that read as "every reload /
 * new window forms a brand-new connection instead of reusing the existing one."
 * Hoisting the fleet out of the component tree + a teardown grace means a quick
 * unmount→remount reuses the LIVE SharedWorker port / direct socket. (Hoisting
 * the *socket*, not the component — no Hydra state is passed down the tree, and
 * nothing connects for an unauthenticated user beyond what already did.)
 *
 * Multi-feed (V3 satellite hydras): g1 consumes the firehose hydra AND any
 * configured hydraedge pipes at the same time (see feedConfig.ts). One
 * HydraClient per feed — each its own SharedWorker instance / upstream socket —
 * all draining into the single, source-agnostic marketStore. The firehose is
 * the "primary": only it drives the global connected / backpressure flags.
 * Ownership is DYNAMIC (edge preferred / firehose fallback): a healthy edge
 * that announces a sport owns its frames + book subscriptions; the moment the
 * edge drops or stops serving it, the firehose — which keeps receiving every
 * sport on its broad subscribe — resumes ingest (marketStore.feedShouldIngest).
 * Exactly one feed ingests each sport at any moment, so both pipes serving a
 * sport is redundancy, not flap.
 *
 * Consumer WS notes: Hydra's consumer WS is on the API port (8891), not :443
 * (the public dashboard). Bearer auth on the socket isn't available from the
 * browser (no custom headers) — see HydraClient. Default firehose URL lives in
 * feedConfig.
 */

import { useEffect } from 'react';
import { HydraClient, type HydraCallbacks } from '../clients/HydraClient';
import { resolveFeeds, type FeedConfig } from '../clients/feedConfig';
import {
  cachedDirectoryDoc,
  directoryFeedsFor,
  fetchDirectory,
  resolveDirectoryFeeds,
  isLatestDirectoryRequest,
  nextDirectoryRequestId,
  shouldAdopt,
  writeCachedDirectory,
  type DirectoryDoc,
} from '../clients/feedDirectory';
import { OpSnapshotStager } from '../lib/opSnapshotStager';
import { useMarketStore } from '../stores/marketStore';
import { useGameStatesStore } from '../stores/gameStatesStore';
import type { UnabatedGameState } from '../services/hydraAnalytics';
import { normalizeSport, SPORT_NORMALIZE } from '../stores/marketFrameNormalization';
import { setFeedNeedsSink, toFeedNeeds } from '../stores/feedNeeds';
import { addOpScopeSink } from '../stores/opNeeds';
import { startAccountFeedNeeds, stopAccountFeedNeeds } from '../stores/accountFeedNeeds';
import { applyFeedFilterUrlOverrides, feedFiltersEnabled, feedFilterModeFor, feedGatewayEnabled } from '../clients/feedFilterConfig';
import { gatewayActive, startGateway, stopGateway } from './feedGateway';
import { BROWSER_CADENCE_MS, FLOOR_STANDBY_CADENCE_MS } from '../workers/feedFilter';

// Resolved once at module load — env is build-time static.
//
// Then the PERSISTED directory is merged in synchronously (localStorage is
// sync), so the fleet dials edges g1 learned on a previous run with zero boot
// latency and without awaiting anything. Resolution order is live directory →
// persisted cache → build-time seed, and rule 6 forbids the live half from
// ever blocking startup: the async refresh lands later and adds whatever is
// new. With no cache and no reachable core this is exactly the env seed.
const FEEDS = resolveFeeds();
const BOOT_DIRECTORY = cachedDirectoryDoc();
if (BOOT_DIRECTORY) FEEDS.push(...directoryFeedsFor(BOOT_DIRECTORY, FEEDS));

// Staleness thresholds for the resnapshot triggers. Background tabs get
// throttled by the browser (Chrome heavily after ~5 min) and the WS can
// die silently — `connected: true` plus a flat `lastMessageMs` is the
// signature. Visibility-return resnaps when we've been quiet for 30 s+
// (catches "I came back to the tab"); the watchdog fires at 60 s+ for
// the rare case where the tab stayed visible but went silent anyway.
const VISIBILITY_RESNAP_STALE_MS = 30_000;
const WATCHDOG_STALE_MS = 60_000;
const WATCHDOG_INTERVAL_MS = 30_000;

// Ordinary navigation is not permission to restart healthy shared feeds.
// Quiet prices, including a whole viewed sport, do not establish transport
// failure. Recover only an individually silent client, using the existing
// visibility-return threshold. Worker upstream watchdogs remain independent.
const CLICK_REFRESH_STALE_MS = VISIBILITY_RESNAP_STALE_MS;
const CLICK_REFRESH_THROTTLE_MS = 15_000;

// ── Module-level singleton fleet ────────────────────────────────────────────
// The fleet lives OUTSIDE the React tree so a component unmount→remount doesn't
// drop and reopen the sockets. `useHydra` ref-counts its mounts: the clients are
// created on the first acquire and torn down only after the last release AND a
// grace window with no re-acquire. A blip (auth-null → RequireAuth drops
// AuthedShell, StrictMode's dev double-mount, a popup open/close) re-acquires
// within the grace window and reuses the live SharedWorker port / direct socket.
const TEARDOWN_GRACE_MS = 10_000;

let _clients: HydraClient[] | null = null;
let _refCount = 0;
// Directory bootstrap fires once per fleet — see ensureFleet.
let _directoryFetched = false;

// Per-feed-id transport generation. Bumped every time a client is built for
// that id, which is what makes a RETIRED transport inert (Round 5, finding 1).
//
// Stopping a client is not enough. `stop()` reports onStatus(false)
// synchronously, but the browser can still deliver the old socket's close event
// afterwards, and that handler is retained by the closure — so a replaced
// endpoint A could fire onStatus(false) AFTER its successor B was live. Since
// callbacks are keyed only by feed id, marketStore would clear B's fresh
// announce and hand the sport back to the firehose. Data callbacks have the
// same exposure: a late frame from A would ingest under B's identity.
const _feedEpoch = new Map<string, number>();

/** Wrap every callback so it no-ops once its transport generation is retired.
 *  Applied to the WHOLE callback set, not just status, because any late frame
 *  from a replaced endpoint would otherwise mutate ownership, routing, health
 *  or market state under the successor's identity. */
function fenceCallbacks(feedId: string, epoch: number, cb: HydraCallbacks): HydraCallbacks {
  const current = () => _feedEpoch.get(feedId) === epoch;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(cb as unknown as Record<string, unknown>)) {
    out[key] = typeof value === 'function'
      ? (...args: unknown[]) => {
          if (!current()) return; // retired transport — speak for nobody
          return (value as (...a: unknown[]) => unknown)(...args);
        }
      : value;
  }
  return out as unknown as HydraCallbacks;
}

/** Build a client for `feed`, claiming the next transport generation for its
 *  id. Every client in the fleet goes through here, so no transport can exist
 *  outside the fence. */
function buildClient(feed: FeedConfig): HydraClient {
  const epoch = (_feedEpoch.get(feed.id) ?? 0) + 1;
  _feedEpoch.set(feed.id, epoch);
  // Negotiate continuity (`evidence: 1` on the subscribe) with every claimant,
  // but do NOT mark the feed evidence-required here: the store promotes it on
  // its first accepted `sourceContinuity` control (noteSourceContinuity). An
  // engine that never answers — the SuperHydra fork edges have no #847
  // producer — stays on legacy age freshness instead of `unconfirmed` for
  // every row it delivers (node1, 2026-09-21 → 09-23).
  const sourceEvidence = feed.role !== 'firehose' && !feed.opOnly;
  return new HydraClient(feed.url, fenceCallbacks(feed.id, epoch, callbacksFor(feed)), {
    workerName: feed.workerName,
    subscribeOpOdds: feed.subscribeOpOdds,
    opOnly: feed.opOnly === true,
    // The dedicated OP transport is QUIET by design once a tab scopes its odds
    // to one game (stores/opNeeds): it may hear nothing but the worker's 5 s
    // liveness tick. The default 5 s stall threshold then RACES that tick —
    // the connection restarted about once a minute (node1, 2026-09-21), each
    // restart failed the OP authority over to the standby, and the marker
    // blinked. Same threshold the narrow lanes use for the same reason.
    ...(feed.opOnly === true ? { tabStallThresholdMs: 45_000 } : {}),
    // Per-role rollout: edges/cores follow VITE_FEED_FILTERS; the firehose
    // (OG today — no Stage A) stays legacy unless VITE_FEED_FILTERS_FIREHOSE.
    feedFilterMode: feedFilterModeFor(feed.role),
    feedSports: feedClaimScope(feed),
    sourceEvidence,
  });
}

/** A claimant's static claim scope as uppercase wire codes — what restricts
 *  the Phase-2 `sports` allow-list on ITS connection. The firehose (and any
 *  feed whose scope is dynamic) passes null = unrestricted: its complement
 *  changes with every announce, and a broad-and-discarded frame is the safe
 *  direction (feedShouldIngest still arbitrates per frame). */
function feedClaimScope(feed: FeedConfig): string[] | null {
  if (feed.role === 'firehose' || feed.opOnly) return null;
  // Every wire spelling that normalizes to an accepted sport (CFB and NCAAF
  // both name college football on the wire) — an allow-list entry the engine
  // never uses is harmless; a missing alias would starve the sport.
  const owned = new Set<string>();
  for (const [code, sport] of Object.entries(SPORT_NORMALIZE)) {
    if (feed.accepts(sport)) owned.add(code.toUpperCase());
  }
  return owned.size > 0 ? [...owned].sort() : null;
}

// The tab's current sports need (null = every sport), kept for the floor
// cadence negotiation below.
let _viewedSports: Set<string> | null = null;

/** Two-tier floor cadence: the core floor is the warm standby (1000 ms) while
 *  every viewed sport an edge claims is actually served by an edge; the
 *  moment the floor is the ONLY source for a viewed sport (edge down / not
 *  announcing) it drops to the browser cadence so failover is as fresh as
 *  today. Re-run on every needs change and every ownership transition. */
function negotiateFloorCadence(): void {
  if (!_clients) return;
  const viewed = _viewedSports == null
    ? null
    : new Set([..._viewedSports].map((c) => normalizeSport(c)).filter((s): s is NonNullable<typeof s> => s != null));
  const sole = store().floorSoleSourceSports(viewed);
  const cadenceMs = sole.length > 0 ? BROWSER_CADENCE_MS : FLOOR_STANDBY_CADENCE_MS;
  FEEDS.forEach((feed, i) => {
    if (feed.role === 'core' && !feed.opOnly) _clients?.[i]?.setMarketFilterPolicy({ cadenceMs });
  });
}

/** Publish the tab's aggregated feed-filter needs to every live client. Each
 *  client restricts the Phase-2 sports axis to its own claim scope; the
 *  class/type opt-ins are connection-agnostic. Inert with the flag off
 *  (setMarketFilterNeeds no-ops). */
function publishFeedNeeds(): void {
  if (!feedFiltersEnabled()) return;
  setFeedNeedsSink((needs) => {
    const declared = toFeedNeeds(needs);
    // The sibling-expanded set (NFL → NFL + NFLPROPS + NFLTOTALS), so the
    // floor-cadence negotiation sees every label the tab actually views.
    _viewedSports = declared.sports === 'all' || declared.sports == null ? null : new Set(declared.sports);
    for (const client of _clients ?? []) client.setMarketFilterNeeds(declared);
    negotiateFloorCadence();
  });
  store().setOwnershipListener(negotiateFloorCadence);
}
/** Publish the tab's OddsPapi game scope to every OP-capable client — active
 *  AND standby ladder members, so a failover lands on a lane already scoped.
 *  Not behind the feed-filter flag: it filters the worker's own fan-out to
 *  this tab and sends nothing upstream. */
let _detachOpScope: (() => void) | null = null;
function publishOpScope(): void {
  // Re-attach so the current scope is re-published to a rebuilt fleet.
  _detachOpScope?.();
  _detachOpScope = addOpScopeSink((games) => {
    for (const client of _clients ?? []) client.setOpScope(games);
  });
}
/** The gateway was tried and could not be reached on this page load — the
 *  fleet serves until a reload. */
let _gatewayGaveUp = false;
let _teardownTimer: ReturnType<typeof setTimeout> | null = null;

// OddsPapi staging (DNL-003, failover rev 2026-08-11). ONE tab-side stager
// instance regardless of transport: in direct mode it's fed raw hydra
// frames, in shared mode worker-synthesized bursts + verbatim-relayed live
// frames — identical semantics either way (the shared lib/opSnapshotStager
// module is the single implementation; the worker holds its own instance for
// greet replay). Committed/accepted changes mirror into the marketStore's
// independent OP map.
//
// ACTIVE-SOURCE GATE: every member of the OP ladder is capable (feedConfig
// `opRank` — the designated authority, then each remaining core in
// VITE_HYDRA_CORE_FEEDS order; the OG firehose is never a member — it has
// no oddspapi lane, Dan 2026-09-09), but exactly ONE is
// ACTIVE at any instant and only its frames reach the stager; the others are
// dropped inert at the gate. Every switch is a stager CONNECTION BOUNDARY
// (notifyDisconnect: generation namespace resets, committed state survives)
// followed by a snapshot re-request from the new source — whose commit
// atomically rebases epochs (contract rule 2e), which is what makes cutting
// between two different producers safe.
let _opStager: OpSnapshotStager | null = null;
let _opActiveFeedId: string | null = null;

// ── Ranked OP failover (2026-08-14) ─────────────────────────────────────────
// Dan's resolver model: hydracore instances are ONE uniform build
// differentiated by config, so the core list is a preference order (hc1, then
// hc2, …) exactly like a resolver's nameserver list. Fall DOWN it on trouble;
// climb back only after the better-ranked instance has been healthy for a
// stability hold, so a restarting HC1 can't flap the ingestion authority.
//
// HEALTH has two independent failure modes and both must count:
//   1. the socket is down — `client.connected`;
//   2. the socket is UP but the instance stopped serving the channel — no
//      oddspapi frame for OP_SILENCE_MS (`client.lastOpFrameMs`). Nothing else
//      in the codebase can see this: lastMessageMs is channel-agnostic and a
//      pong keeps it warm forever.
//
// The two directions are deliberately ASYMMETRIC:
//   DOWN (failure) — take the best-ranked member whose socket is up, with no
//     hold and no proof of service. An outage must not wait out a timer, and a
//     connected-but-silent lane cannot corrupt anything: nothing reaches the
//     store until a burst COMMITS in the stager.
//   UP (recovery) — a better-ranked member must PROVE it is serving the
//     channel (a real oddspapi frame, not merely an open socket) and sustain
//     that for OP_HOLD_MS. This is what stops a restarting HC1 from flapping
//     the authority, and it is why a listed-but-not-yet-live instance (hc2 on
//     day one) never wins the slot by simply existing.
// Silence therefore demotes, but never promotes.
const OP_SILENCE_MS = 120_000;
const OP_HOLD_MS = 30_000;
const OP_EVAL_INTERVAL_MS = 5_000;
// Boot grace: "has not connected YET" is not the same failure as "was
// connected and dropped". Without this the designated authority loses the slot
// at startup to whichever member's socket opens first — and then has to serve
// out a full stability hold to take back a slot it never legitimately lost.
// Applies only until a member has been seen connected once; after that, a drop
// fails over immediately (which is the whole point — no gap).
const OP_BOOT_GRACE_MS = 15_000;

// Ladder members in rank order, paired with their fleet index (the
// `_clients[i] ↔ FEEDS[i]` invariant). Computed once — FEEDS is module-static.
const OP_LADDER: Array<{ feed: FeedConfig; idx: number }> = FEEDS
  .map((feed, idx) => ({ feed, idx }))
  .filter((m) => m.feed.subscribeOpOdds && m.feed.opRank != null)
  .sort((a, b) => (a.feed.opRank ?? 0) - (b.feed.opRank ?? 0));

// Wall-clock since which a ladder member has been continuously healthy, keyed
// by feed id (absent = not currently healthy). Drives the stability hold.
const _opHealthySince = new Map<string, number>();
// Ladder members seen connected at least once this fleet — see OP_BOOT_GRACE_MS.
const _opEverConnected = new Set<string>();
let _opEvalTimer: ReturnType<typeof setInterval> | null = null;
// When the current member took the authority slot. Bounds the grace given to a
// member that has never delivered a frame: without it, a core that connects
// but never serves oddspapi would hold the slot forever (its silence clock
// never starts) while a healthy lower-ranked instance sat unused.
let _opActiveSinceMs = 0;

const store = () => useMarketStore.getState();

function activeOpClient(): HydraClient | null {
  if (!_clients || _opActiveFeedId == null) return null;
  const idx = FEEDS.findIndex((f) => f.id === _opActiveFeedId);
  return idx >= 0 ? _clients[idx] : null;
}

/** Is this ladder member serving the oddspapi channel right now? Socket up AND
 *  a frame within OP_SILENCE_MS. A member that has NEVER delivered (0) is not
 *  serving — that is the listed-but-not-yet-live instance, and it must not win
 *  the authority slot by merely holding an open socket. */
function opMemberServing(client: HydraClient, now: number): boolean {
  return client.connected && client.lastOpFrameMs > 0 && now - client.lastOpFrameMs <= OP_SILENCE_MS;
}

/** KNOWN-stale: connected, has delivered on THIS connection, and that delivery
 *  has aged out. Distinct from "unknown" (`lastOpFrameMs === 0` — never
 *  delivered since the last connection boundary), which stays an eligible
 *  failover target because it may simply not have spoken yet. Descending onto a
 *  lane we have positive evidence is dead just moves the outage. */
function opMemberKnownStale(client: HydraClient, now: number): boolean {
  return client.connected && client.lastOpFrameMs > 0 && now - client.lastOpFrameMs > OP_SILENCE_MS;
}

/** Recompute which ladder member is ACTIVE. Called from every capable feed's
 *  onStatus transition AND from the periodic evaluation tick (silence and the
 *  stability hold are both time-based, and onStatus alone never fires for
 *  them). Idempotent under repeated identical events — onStatus is not
 *  deduped, so stop()/restartShared()/forceReconnect() all re-emit `false`. */
function reevaluateOpAuthority(): void {
  if (!_clients || OP_LADDER.length === 0) return;
  try {
    selectOpAuthority();
  } finally {
    // AFTER the switch, so health describes the member now holding the slot.
    publishOpFeedHealth();
  }
}

/** Health of the ACTIVE member, for the ladder's fair dot. Same two failure
 *  modes and the same thresholds the controller itself fails over on, so the
 *  marker can never claim "live" while the controller believes the lane dead —
 *  and "never delivered on this connection yet" stays live, exactly as it
 *  stays an eligible failover target. */
function publishOpFeedHealth(): void {
  const client = activeOpClient();
  if (client == null) return;
  const now = Date.now();
  store().noteOpFeedHealth(
    !client.connected ? 'down' : opMemberKnownStale(client, now) ? 'silent' : 'live',
  );
}

function selectOpAuthority(): void {
  if (!_clients || OP_LADDER.length === 0) return;
  const clients = _clients;
  const now = Date.now();

  // Track continuous health for the stability hold. "Healthy" here is the
  // promotion-grade notion: connected AND proven to be serving the channel.
  for (const { feed, idx } of OP_LADDER) {
    if (clients[idx].connected) _opEverConnected.add(feed.id);
    if (opMemberServing(clients[idx], now)) {
      if (!_opHealthySince.has(feed.id)) _opHealthySince.set(feed.id, now);
    } else {
      _opHealthySince.delete(feed.id);
    }
  }

  const activeMember = OP_LADDER.find((m) => m.feed.id === _opActiveFeedId) ?? null;
  const activeClient = activeMember ? clients[activeMember.idx] : null;
  // The active member holds the slot while its socket is up and it hasn't gone
  // silent. Note connected-but-silent is a demotion trigger only when someone
  // else can actually take over — going dark on a hunch is strictly worse than
  // holding stale-but-real data.
  const activeStillGood =
    activeClient == null
      ? false
      : !activeClient.connected
        // Still dialling for the first time? Hold the slot — see
        // OP_BOOT_GRACE_MS. A member that HAS connected before and is now down
        // has genuinely failed, and fails over with no grace at all.
        ? activeMember != null &&
          !_opEverConnected.has(activeMember.feed.id) &&
          now - _opActiveSinceMs <= OP_BOOT_GRACE_MS
        : activeClient.lastOpFrameMs > 0
          ? now - activeClient.lastOpFrameMs <= OP_SILENCE_MS
          // Connected but nothing delivered on THIS connection: a bounded grace
          // so a freshly promoted (or freshly reconnected) member gets time to
          // send its first burst, while a permanently mute one eventually
          // yields. Anchored at the later of "took the slot" and "this
          // connection opened" — a reconnect must not inherit a tenure clock
          // that already expired and get demoted before it can speak.
          : now - Math.max(_opActiveSinceMs, activeClient.connectedSinceMs) <= OP_SILENCE_MS;

  // A better-ranked member may take over only after PROVING service for the
  // full stability hold — this is what stops a restarting HC1 from flapping
  // the authority the instant its socket comes back.
  const readyPromotion = OP_LADDER.find((m) => {
    if (activeMember != null && m.idx === activeMember.idx) return false;
    const since = _opHealthySince.get(m.feed.id);
    if (since == null || now - since < OP_HOLD_MS) return false;
    return activeMember == null || (m.feed.opRank ?? 0) < (activeMember.feed.opRank ?? 0);
  }) ?? null;

  let next: { feed: FeedConfig; idx: number } | null = null;
  if (activeStillGood) {
    // Hold the slot unless a BETTER-ranked member has earned it back.
    next = readyPromotion ?? activeMember;
  } else {
    // The active member failed. Two steps, in order:
    //
    // 1. Any member PROVEN to be serving right now wins immediately, in either
    //    rank direction. The stability hold exists to stop a flapping engine
    //    from disturbing a HEALTHY authority; with the authority already
    //    failed there is nothing to protect, and making a proven-live engine
    //    wait 30s would just extend the outage.
    // 2. Otherwise descend MONOTONICALLY — only past the current rank, and
    //    only onto lanes we have no evidence are dead. Both halves matter:
    //    without rank progression two silent cores hand the slot back and
    //    forth forever (the controller alternates hc1→hc2→hc1) and a healthy
    //    lower-ranked core is never reached; without the staleness exclusion the
    //    descent stops at the first open socket even when its channel is
    //    known dead. "Unknown" lanes stay eligible — never having spoken on
    //    this connection is not evidence of death.
    //
    // If neither finds anything, stay put and keep rendering the last
    // committed values rather than going dark.
    const activeRank = activeMember?.feed.opRank ?? -1;
    next = OP_LADDER.find((m) => m.idx !== activeMember?.idx && opMemberServing(clients[m.idx], now))
      ?? OP_LADDER.find((m) =>
        (m.feed.opRank ?? 0) > activeRank &&
        clients[m.idx].connected &&
        !opMemberKnownStale(clients[m.idx], now))
      ?? activeMember;
  }
  if (next == null) return;

  const nextId = next.feed.id;
  if (nextId === _opActiveFeedId) {
    // No switch — but an ACTIVE feed going down with no alternative is
    // still a connection boundary (generations are per-connection).
    if (!clients[next.idx].connected) _opStager?.notifyDisconnect();
    return;
  }
  _opActiveFeedId = nextId;
  _opActiveSinceMs = now;
  _opStager?.notifyDisconnect();
  clients[next.idx].requestOpResnapshot();
  store().noteOpActiveFeed(nextId);
}

function ensureOpStager(): OpSnapshotStager {
  if (_opStager) return _opStager;
  _opStager = new OpSnapshotStager({
    onCommit: (frames) => {
      // A STANDBY with nothing to say cannot withdraw coverage it never
      // provided. `odds@account` is a lease that lives on ONE core; every other
      // core serves the channel EMPTY. When the authority fails over to such a
      // standby, its snapshot commits as "no games" — rule 9 reads that as
      // every game withdrawn, and the ladder's fair-odds marker vanished until
      // the real source won the slot back ~30 s later. An empty commit is
      // honoured only from the DESIGNATED source; from a standby the last
      // committed state stands (it cannot move, and the marker's dead-feed
      // state says so if the real source stays gone).
      const preferredId = FEEDS.find((f) => f.opPreferred)?.id ?? null;
      if (
        frames.length === 0 &&
        preferredId != null &&
        _opActiveFeedId !== preferredId &&
        store().getAnyOpBookmaker() != null
      ) {
        console.warn('[useHydra] ignoring EMPTY oddspapi snapshot from a standby source — keeping committed odds');
        return;
      }
      store().replaceOpOdds(frames.map((f) => f.op));
    },
    onLiveUpsert: (frame) => store().ingestOpOdds(frame.op),
    onLiveDelete: (gameKeyV2) => store().deleteOpOdds(gameKeyV2),
    onRequestSnapshot: () => activeOpClient()?.requestOpResnapshot(),
  });
  return _opStager;
}

// Per-feed callbacks. Market-frame ingest uses DYNAMIC ownership
// (marketStore.feedShouldIngest): the edge is always preferred for the sports
// it claims and announces; the firehose is the warm fallback that resumes a
// sport the moment its edge drops or stops serving it. The firehose alone
// drives the global connected / backpressure flags; edge connection
// transitions feed noteFeedStatus (re-route + backfill). activeSports is
// filtered to each feed's static claim scope and keyed by feed id so
// getActiveSports unions the firehose + edges instead of clobbering.
function callbacksFor(feed: FeedConfig): HydraCallbacks {
  const isFirehose = feed.role === 'firehose';
  return {
    onSourceContinuity: (control, invalidLane) => store().noteSourceContinuity(feed.id, control, invalidLane),
    onMarket: (market, age) => {
      const sport = normalizeSport(market.sport ?? '');
      // Edge frames establish per-(sport, venue) ownership by their own
      // arrival — recorded BEFORE the gate so the frame that proves delivery
      // is also the first one ingested under edge ownership.
      if (!isFirehose) store().noteFeedDelivery(feed.id, sport, market.source);
      if (store().feedShouldIngest(feed.id, sport, market.source, market.ticker)) {
        store().ingestMarket(market, feed.id, age);
      }
    },
    onOrderbook: (ticker, book, age, observation) => store().ingestOrderbook(ticker, book, feed.id, age, observation),
    onConsensus: (consensus, serverTs, age) => store().ingestConsensus(consensus, serverTs, feed.id, age),
    // Game state rides the WS instead of a 5 s REST poll. No feed gate: the
    // frame is absolute and rev-ordered per key, so two feeds delivering the
    // same game converge rather than fight — the store's rev guard makes a
    // late frame from a slower socket a no-op instead of a revert.
    onGameState: (msg) => {
      if (typeof msg.ticker !== 'string' || !msg.ticker) return;
      useGameStatesStore.getState().applyWireState(
        msg.ticker,
        (msg.state as UnabatedGameState | null) ?? null,
        { rev: msg.rev, sourceEpoch: msg.sourceEpoch, serverTs: msg.serverTs },
      );
      // Fan the event start into marketStore's canonical-key → startTime cache,
      // exactly as the poll did — spreads/totals and Poly-less games depend on
      // it for GTS expiry, countdown chips and the near-tip pulse.
      const st = msg.state as { eventStart?: string } | null;
      if (st?.eventStart) {
        const ms = Date.parse(st.eventStart);
        if (Number.isFinite(ms)) store().seedEventStartTimeMsByKey(msg.ticker, ms);
      }
    },
    onActiveSports: (sports) => {
      // A discovered edge's announce is AUTHORITATIVE over the directory's
      // advisory list, so it widens that feed's claim scope before the filter
      // runs (G1-DIR-01). Without this the filter below would reject the very
      // announcement that should have established the scope, and an edge
      // published `live:false, sports:[]` — the normal shape for one that has
      // not reported yet — could never serve anything. Static feeds have no
      // such hook: their scope is operator config and stays fixed.
      const announced = sports
        .map((code) => normalizeSport(code))
        .filter((s): s is NonNullable<typeof s> => s != null);
      feed.noteAnnouncedSports?.(announced);
      // D10 floor: a CORE's announce reaches the store UNFILTERED — the
      // floor gate and the chip display need the full set, and it cannot
      // create ownership (_edgeServing still requires accepts()). Edges
      // keep the claim-scope filter unchanged.
      const owned = feed.role === 'core'
        ? sports
        : sports.filter((code) => feed.accepts(normalizeSport(code)));
      store().ingestActiveSports(owned, feed.id);
    },
    onStatus: (isConnected) => {
      if (isFirehose) store().setConnected(isConnected);
      else store().noteFeedStatus(feed.id, isConnected);
      // OP failover controller: every capable feed's connection transition
      // re-evaluates which source is ACTIVE. The controller owns the
      // stager's connection-boundary calls (switches AND active-feed drops)
      // — a STANDBY's disconnect must never reset the active namespace.
      if (feed.subscribeOpOdds) reevaluateOpAuthority();
    },
    // OddsPapi frames → the shared stager, through the ACTIVE-SOURCE GATE:
    // capable feeds (preferred + OG standby) get callbacks, but only the
    // active feed's frames reach the stager — the standby's are inert here
    // (and every non-capable feed's client drops them before this layer —
    // DNL-003 belt and suspenders). Raw wire messages in; the stager owns
    // all staging / rev / epoch / generation semantics.
    onOpOdds: feed.subscribeOpOdds
      ? (msg) => {
          if (feed.id !== _opActiveFeedId) return;
          ensureOpStager().frame(msg);
        }
      : undefined,
    onOpSnapshotBegin: feed.subscribeOpOdds
      ? (msg) => {
          if (feed.id !== _opActiveFeedId) return;
          ensureOpStager().begin(msg);
        }
      : undefined,
    onOpSnapshotEnd: feed.subscribeOpOdds
      ? (msg) => {
          if (feed.id !== _opActiveFeedId) return;
          ensureOpStager().end(msg);
        }
      : undefined,
    // Firehose-only, matching connected/backpressure: an edge feed's pongs
    // must not mask a dead firehose on the global feed-health dot.
    onKeepalive: isFirehose ? () => store().noteKeepalive() : undefined,
    onBackpressure: (level) => {
      if (isFirehose) store().setBackpressureLevel(level);
    },
    // Consumer feed filters (Stage C): the ONE atomic reset per installed
    // generation — the transport guarantees it precedes every frame of that
    // generation, so the store retires out-of-scope rows before the
    // replacement snapshot repopulates. Acks are recorded for the health /
    // verification surfaces (FeedTape, G1 Health).
    onFilterReset: (reset) => {
      store().applyFilterReset(feed.id, reset.scope, reset);
    },
    onFilterAck: (ack) => store().noteFeedFilterAck(feed.id, ack),
    // Games-axis release gate (hydra review DNL-003): the engines withdraw an
    // already-delivered row whose identity change makes it fail the installed
    // filter. Both transports fence it before it gets here, so this is an
    // unconditional eviction of the ticker's row state; the store still
    // checks provenance and the stamped pair before deleting.
    onMarketRemoved: (ticker, pair) => {
      store().applyMarketRemoved(feed.id, ticker, pair);
    },
  };
}

/** Publish the fleet to the marketStore so each view's orderbook needs route to
 *  the client owning the ticker's sport. Re-called when the directory adds an
 *  edge — setFeedClients already handles rewiring (it prunes announce state for
 *  vanished ids and re-routes book needs). */
function registerFleet(clients: HydraClient[]): void {
  store().setFeedClients(
    FEEDS.map((feed, i) => ({
      id: feed.id,
      client: clients[i],
      accepts: feed.accepts,
      role: feed.role,
      label: feed.label,
      url: feed.url,
      opAuthority: feed.subscribeOpOdds,
      opOnly: feed.opOnly === true,
      opPreferred: feed.opPreferred === true,
      opRank: feed.opRank,
    })),
  );
}

/**
 * Merge a directory into the RUNNING fleet. Additive only, so nothing is ever
 * torn down: an edge already configured is skipped, and a genuinely new one
 * gets its own client and its own SharedWorker name (the worker latches its
 * upstream URL per name, which is why re-pointing would need a teardown but
 * adding does not).
 *
 * Returns how many edges were added. Safe before the fleet exists — the feed
 * list still grows and ensureFleet picks it up.
 */
function applyDirectoryEdges(doc: DirectoryDoc): number {
  const { added, moved, retired, assignments } = resolveDirectoryFeeds(doc, FEEDS);
  // Core's DIRECTION is registered even when the fleet itself is unchanged —
  // an assignment can move ownership between engines g1 is already connected
  // to, which is the whole point of act mode.
  store().noteDirectoryAssignments(assignments);
  if (added.length === 0 && moved.length === 0 && retired.length === 0) return 0;

  // A discovered edge whose new endpoint is ALREADY dialled by someone else
  // (a static feed, or a sibling) must not be duplicated — but the stale
  // transport still has to go, or it runs until reload (Round 5, finding 2).
  // Splicing is safe here and only here: discovered feeds are always appended
  // after the static ones and never join OP_LADDER, so the ladder's precomputed
  // indices cannot be disturbed.
  for (const feedId of retired) {
    const idx = FEEDS.findIndex((f) => f.id === feedId);
    if (idx < 0 || !FEEDS[idx].id.startsWith('dir:edge:')) continue;
    _feedEpoch.set(feedId, (_feedEpoch.get(feedId) ?? 0) + 1); // fence its callbacks first
    FEEDS.splice(idx, 1);
    if (_clients) {
      _clients[idx].stop();
      _clients.splice(idx, 1);
    }
  }

  // A MOVED edge is replaced IN PLACE, by logical identity (G1-DIR-03). In
  // place matters twice over: it keeps the `_clients[i] ↔ FEEDS[i]` invariant
  // that OP_LADDER's precomputed indices rely on, and it leaves exactly one
  // entry per edgeId — appending instead produced two feeds sharing an id and
  // a worker name, where the worker's first-URL latch could keep the STALE
  // endpoint authoritative and the new one never connect.
  //
  // Retiring the old transport is not un-learning the edge: the edge is
  // retained, at its current address. The cache already understood this; the
  // fleet now agrees with it.
  for (const feed of moved) {
    const idx = FEEDS.findIndex((f) => f.id === feed.id);
    if (idx < 0) { added.push(feed); continue; } // vanished under us — treat as new
    FEEDS[idx] = feed;
    if (_clients) {
      const old = _clients[idx];
      // buildClient claims the next generation for this id, which retires the
      // old transport's callbacks BEFORE it is stopped — so neither stop()'s
      // own onStatus(false) nor the socket's later close event can revoke the
      // successor.
      _clients[idx] = buildClient(feed);
      old.stop();
      _clients[idx].start();
    }
  }

  FEEDS.push(...added);
  if (!_clients) return added.length + moved.length + retired.length;
  for (const feed of added) {
    const client = buildClient(feed);
    _clients.push(client);
    client.start();
  }
  registerFleet(_clients);
  // New/moved transports start from the default filter; hand them the tab's
  // current opt-ins (re-installing the sink republishes to every client).
  publishFeedNeeds();
  publishOpScope();
  return added.length + moved.length + retired.length;
}

/**
 * Fetch the directory from the highest-ranked reachable core and merge it.
 *
 * Staleness is decided CAUSALLY, in two steps that must stay in this order:
 *
 *   1. Request correlation — the response is applied only if it answers the
 *      LATEST issued request. A slow response overtaken by a newer one is
 *      dropped here, before anything reads its contents, which is what makes a
 *      retired incarnation arriving late a non-event rather than a revert.
 *   2. Lineage supersession (`shouldAdopt`) — same (publisher, incarnation)
 *      requires a strictly greater generation; a DIFFERENT incarnation is
 *      adopted outright, because it answered the latest request and which
 *      lineage is "newer" is not something the payload can settle.
 *
 * Nothing here compares incarnations for order or consults a clock. A core
 * that lost its state file republishes at generation 1 under a new
 * incarnation with strictly newer content — possibly with the host clock
 * moved backwards — and g1 must adopt it.
 *
 * Failure is a NON-EVENT by design (rule 6): no core reachable, a malformed
 * answer, a superseded response, or a stale generation all leave the fleet
 * exactly as it is, running on cache or env.
 */
export async function refreshFeedDirectory(): Promise<number> {
  const requestId = nextDirectoryRequestId();
  const result = await fetchDirectory(requestId);
  if (!result) return 0;
  if (!isLatestDirectoryRequest(result.requestId)) return 0; // superseded in flight
  if (!shouldAdopt(result.doc, cachedDirectoryDoc())) return 0;
  writeCachedDirectory(result.doc);
  return applyDirectoryEdges(result.doc);
}

/** Create the fleet once + register it with the marketStore so each view's
 *  orderbook needs route to the client that owns the ticker's sport. Idempotent. */
function ensureFleet(): HydraClient[] {
  if (_clients) return _clients;
  // Runtime feed-filter overrides (`?feedFilters=on` …) are folded into
  // localStorage HERE, before any client resolves its mode — so a URL flag
  // takes effect on the same page load, no rebuild, no env edit.
  applyFeedFilterUrlOverrides();
  // Feed gateway: ONE socket, scoped server-side — and EXCLUSIVE with the
  // fleet (two transports ingesting the same rows would flap provenance). The
  // fleet is the fallback: if the gateway cannot be reached, it is torn down
  // completely and only then is the fleet built.
  if (feedGatewayEnabled() && !_gatewayGaveUp) {
    startGateway(() => {
      console.warn('[useHydra] feed gateway unreachable — falling back to the hydra fleet');
      _gatewayGaveUp = true;
      teardownFleet();
      if (_refCount > 0) ensureFleet();
    });
    _clients = [];
    return _clients;
  }
  const clients = FEEDS.map((feed) => buildClient(feed));
  // Initial OP active source = the env-preferred feed (resolveFeeds stamps
  // exactly one); the failover controller re-points it on connection
  // transitions (preferred down → OG standby → back).
  _opActiveFeedId = FEEDS.find((f) => f.opPreferred)?.id ?? null;
  _opActiveSinceMs = Date.now();
  _opHealthySince.clear();
  _opEverConnected.clear();
  if (_opActiveFeedId != null) store().noteOpActiveFeed(_opActiveFeedId);
  registerFleet(clients);
  // LAST KNOWN OWNERSHIP survives a restart with every core unreachable: the
  // cached directory carries core's assignments, so they are re-applied here
  // rather than waiting on a fetch that may never succeed. Cold cache ⇒ no
  // assignments ⇒ env decides, which is the intended fallback, not darkness.
  if (BOOT_DIRECTORY) {
    store().noteDirectoryAssignments(resolveDirectoryFeeds(BOOT_DIRECTORY, FEEDS).assignments);
  }
  for (const client of clients) client.start();
  _clients = clients;
  // Feed-filter needs: publish the tab's union to the fleet (and keep
  // publishing on change), and let the account plane declare its own.
  publishFeedNeeds();
  publishOpScope();
  if (feedFiltersEnabled()) startAccountFeedNeeds();
  // Silence detection and the stability hold are time-based, and onStatus —
  // the only existing trigger — never fires for either. This tick lives at
  // MODULE scope (like _teardownTimer), deliberately not on the per-mount
  // watchdog: that one skips while the tab is hidden, and a backgrounded desk
  // tab must still fail over.
  if (_opEvalTimer == null && OP_LADDER.length > 1) {
    _opEvalTimer = setInterval(reevaluateOpAuthority, OP_EVAL_INTERVAL_MS);
  }
  // Directory bootstrap, AFTER the fleet is already connecting — never before,
  // and never awaited (rule 6). Once per fleet, not once per mount: useHydra's
  // effect re-runs on every remount (auth blips, StrictMode) and this is a
  // boot-time question. Skipped under test — vitest inherits the developer's
  // real .env.local, so an unguarded fetch here reaches whatever hydracore
  // happens to be running on their box from every suite that mounts this hook;
  // suites drive `refreshFeedDirectory()` explicitly instead.
  if (!_directoryFetched && import.meta.env.MODE !== 'test' && typeof fetch === 'function') {
    _directoryFetched = true;
    void refreshFeedDirectory().catch(() => {});
  }
  return clients;
}

/** A mount acquired the fleet: bump the refcount, cancel any pending teardown. */
function acquireFleet(): HydraClient[] {
  _refCount += 1;
  if (_teardownTimer) {
    clearTimeout(_teardownTimer);
    _teardownTimer = null;
  }
  return ensureFleet();
}

/** A mount released the fleet: drop the refcount; tear down only after a grace
 *  window passes with no re-acquire, so a quick remount reuses the live socket. */
function releaseFleet(): void {
  _refCount = Math.max(0, _refCount - 1);
  if (_refCount > 0 || _teardownTimer) return;
  _teardownTimer = setTimeout(() => {
    _teardownTimer = null;
    if (_refCount > 0) return; // re-acquired during the grace window
    teardownFleet();
  }, TEARDOWN_GRACE_MS);
}

/** Stop + unregister the fleet. Driven by the grace timer (last consumer gone)
 *  or by a real page unload (pagehide). */
function teardownFleet(): void {
  if (_teardownTimer) {
    clearTimeout(_teardownTimer);
    _teardownTimer = null;
  }
  if (_opEvalTimer) {
    clearInterval(_opEvalTimer);
    _opEvalTimer = null;
  }
  if (!_clients) return;
  if (gatewayActive()) {
    stopGateway();
    _clients = null;
    return;
  }
  setFeedNeedsSink(null);
  _detachOpScope?.();
  _detachOpScope = null;
  store().setOwnershipListener(null);
  stopAccountFeedNeeds();
  store().setFeedClients([]);
  for (const client of _clients) client.stop();
  _clients = null;
  _opActiveFeedId = null;
  _opActiveSinceMs = 0;
  _opHealthySince.clear();
  _opEverConnected.clear();
  // Drop the stager with the fleet — a rebuilt fleet starts a fresh
  // staging lifecycle (its connect snapshot repopulates the store).
  _opStager?.notifyDisconnect();
  _opStager = null;
}

/** A non-persisted pagehide drains the fleet (fast needs-drop) but the document
 *  does NOT always actually unload — iOS app-background, a cancelled navigation,
 *  a blocked beforeunload all fire pagehide(persisted=false) yet keep the same JS
 *  context with NO React remount. When such a page comes back (pageshow /
 *  visibility-return), rebuild the fleet if there are still live mounts but it
 *  was drained. Without this the desk would sit on a dead socket forever. Returns
 *  true if it rebuilt. */
function reviveIfNeeded(): boolean {
  if (_refCount > 0 && !_clients) {
    ensureFleet();
    return true;
  }
  return false;
}

export function useHydra() {
  const connected = useMarketStore((s) => s.connected);
  const marketsCount = useMarketStore((s) => s.marketsCount);
  const orderbooksCount = useMarketStore((s) => s.orderbooksCount);

  useEffect(() => {
    acquireFleet();

    // Fast-path drain on tab close. `pagehide` is more reliable than
    // `beforeunload` (fires on mobile + iOS Safari, doesn't break bfcache)
    // and lets each SharedWorker drop our orderbook needs from the union
    // immediately instead of waiting on its 30 s heartbeat reaper.
    //
    // Bfcache caveat: when `event.persisted` is true the page is being
    // snapshotted for back/forward cache, NOT torn down — calling teardown
    // there permanently orphans the tab. Skip it and rely on the worker's
    // reaper-resurrect path + the tab-side stall detector to recover. A real
    // (non-persisted) pagehide discards the document, so tear the whole
    // singleton down, not just this mount's ref.
    const handlePageHide = (event: PageTransitionEvent) => {
      if (event.persisted) return;
      teardownFleet();
    };
    window.addEventListener('pagehide', handlePageHide);

    // Return path. (a) A non-persisted pagehide drained us but the document
    // survived (iOS background, cancelled nav) → reviveIfNeeded rebuilds and
    // returns true (the fresh fleet is already started — nothing more to do).
    // (b) bfcache restore (persisted) → the fleet is still live but its port may
    // have been reaped during the freeze, so force a clean restart.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (reviveIfNeeded()) return;
      if (!event.persisted) return;
      // Same-generation reattach, NEVER an escape: a bfcache return is a
      // healthy-path event, and DNL-R13-P1 was exactly this call minting a
      // replacement worker on every back/forward restoration.
      for (const client of _clients ?? []) client.reattachShared('bfcache restore');
    };
    window.addEventListener('pageshow', handlePageShow);

    // Heal stale-on-return per feed: when the tab becomes visible after time
    // in the background, ask each upstream we haven't heard from in
    // VISIBILITY_RESNAP_STALE_MS+ for a fresh markets snapshot. Cheap when
    // fresh; the threshold gates out rapid alt-tabs.
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      // A survived-pagehide may have drained us while hidden — rebuild on return.
      reviveIfNeeded();
      for (const client of _clients ?? []) {
        const lastMs = client.lastMessageMs;
        if (lastMs === 0) continue;
        if (Date.now() - lastMs > VISIBILITY_RESNAP_STALE_MS) client.requestResnapshot();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Watchdog for silent stalls while the tab stays visible — per feed, since
    // one pipe can wedge while another keeps flowing. Skip while hidden to
    // avoid waking a deliberately-throttled background tab.
    const watchdog = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      for (const client of _clients ?? []) {
        const lastMs = client.lastMessageMs;
        if (lastMs === 0) continue;
        if (Date.now() - lastMs > WATCHDOG_STALE_MS) client.requestResnapshot();
      }
    }, WATCHDOG_INTERVAL_MS);

    // Recover a silent feed without tearing down the healthy siblings. The
    // previous activity-fraction heuristic reconnected the whole fleet on a
    // normal navigation through quiet markets, including all other tabs that
    // shared those workers. Data activity and transport liveness are separate.
    let lastClickRefreshAt = 0;
    const handleClickRefresh = () => {
      const now = Date.now();
      if (now - lastClickRefreshAt < CLICK_REFRESH_THROTTLE_MS) return;
      // Never the core floor: it is the warm tier (every sport at 1 s) and a
      // forced refresh there is a ~20k-row replacement snapshot for nothing
      // the click was about. Edges + firehose only.
      FEEDS.forEach((feed, i) => {
        if (feed.role === 'core') return;
        const client = _clients?.[i];
        if (!client || client.lastMessageMs === 0 || now - client.lastMessageMs <= CLICK_REFRESH_STALE_MS) return;
        lastClickRefreshAt = now;
        client.forceReconnect();
      });
    };
    document.addEventListener('pointerdown', handleClickRefresh, { capture: true });

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('pointerdown', handleClickRefresh, { capture: true });
      clearInterval(watchdog);
      releaseFleet();
    };
  }, []);

  return { connected, marketsCount, orderbooksCount };
}

/** Snapshot of the live fleet's per-feed transport — read by G1 Health to show
 *  whether THIS browser shares one upstream WS across its tabs (SharedWorker) or
 *  opened a direct socket per window (the fallback that reads as "new connection
 *  every reload/window"). Empty before the first mount connects. */
/** A props surface opened (Player Props / Game Props tab): lift the shared
 *  workers' prop-frame discard across the whole fleet and backfill. Safe
 *  before the fleet exists (no-op) and idempotent per worker. */
export function requestPropsFeed(): void {
  if (!_clients) return;
  for (const c of _clients) c.requestPropsFeed();
}

/** Read-only view of the fleet, for diagnostics. `_clients` is module-private,
 *  so this is the only window onto it — G1Health's transport table renders from
 *  here, and the counters below let a reader tell "connected but silent" from
 *  "connected and delivering" without opening DevTools. Reading a counter
 *  cannot change what it counts; nothing here has a side effect. */
export function getFeedTransports(): Array<{
  id: string;
  role: string;
  label?: string;
  url: string;
  transport: 'shared' | 'direct' | 'connecting';
  connected: boolean;
  marketsReceived: number;
  orderbooksReceived: number;
  /** Wall clock of the last frame on this feed, or 0 before the first. */
  lastMessageMs: number;
}> {
  const clients = _clients;
  if (!clients) return [];
  return FEEDS.map((feed, i) => ({
    id: feed.id,
    role: feed.role,
    label: feed.label,
    url: feed.url,
    transport: clients[i].transport,
    connected: clients[i].connected,
    marketsReceived: clients[i].marketsReceived,
    orderbooksReceived: clients[i].orderbooksReceived,
    lastMessageMs: clients[i].lastMessageMs,
  }));
}
